import { createContext, use, useCallback, type ReactNode } from "react";
import { useScopeParams } from "../hooks/useScopeParams";
import { analyticsService } from "@/services/analyticsService";

type PageViewEvent = {
  event: "PAGE_VIEW";
  pageName: string;
  referrer: string;
  button?: string;

  metadata?: Record<string, any>;
};

type ButtonClickEvent = {
  event: "BUTTON_CLICK";
  pageName: string;
  referrer: string;
  button: string;

  metadata: Record<string, any>;
};

type AnalyticsEvent = PageViewEvent | ButtonClickEvent;

type EnrichedAnalyticsEvent = AnalyticsEvent & {
  timestamp: string;
  trial_id?: string;
};

type AnalyticsContextValue = {
  logEvent: (event: AnalyticsEvent) => void;
};

type AnalyticsProviderProps = {
  children: ReactNode;
};

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { scope, id } = useScopeParams();

  const logEvent = useCallback(
    (event: AnalyticsEvent) => {
      const enrichedEvent: EnrichedAnalyticsEvent = {
        ...event,
        timestamp: new Date().toISOString(),
        // Only a trial-scoped route carries a real trial in the id segment. On
        // /portfolio/<screen> the id is the screen name, and sending it made the
        // backend get_object_or_404 on a trial that cannot exist.
        ...(scope === "trial" && id ? { trial_id: id } : {}),
      };
      // Fire-and-forget: this promise is deliberately not awaited, so a rejection
      // would escape the surrounding scope as an unhandled rejection and take the
      // whole screen down via the error boundary. Analytics must never do that.
      analyticsService.sentUserEvents(enrichedEvent).catch((error: unknown) => {
        console.warn("Failed to send analytics event:", error);
      });
    },
    [scope, id],
  );

  const contextValue: AnalyticsContextValue = {
    logEvent,
  };

  return <AnalyticsContext value={contextValue}>{children}</AnalyticsContext>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAnalytics = (): AnalyticsContextValue => {
  const context = use(AnalyticsContext);

  if (context === null) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }

  return context;
};
